import { makeResource } from '@/lib/api/resources';
import { api } from '@/lib/api/client';
import type { BlockSchemaRegistry, ContentItem } from '@/lib/api/types';

/** One resource client per collection key, base `/admin/{collection}` (spec §9.3). */
export function contentResource(collection: string) {
  return makeResource<ContentItem>(`/admin/${collection}`);
}

/** One row of a saved order, as `PUT /admin/{collection}/order` answers it. */
export interface OrderEntry {
  id: string;
  ident: string;
  sort_order: number;
}

/**
 * Saves a collection's hand-made order (round 8, R8-34). `ids` is the FULL list of the
 * collection's items, first to last — the server refuses a partial one with a 422.
 */
export function saveOrder(collection: string, ids: string[]): Promise<{ items: OrderEntry[] }> {
  return api.put<{ items: OrderEntry[] }>(`/admin/${collection}/order`, { ids });
}

/**
 * A place an item of a collection with placements shows (round 8, R8-15), and for the
 * scoped place (a course page) which one. The places themselves are served by
 * `GET /admin/capabilities` (`collections[].placements`), so this console holds no list of
 * them: a place the backend adds is offered here on the next load.
 */
export type Placement = string;
export interface PlaceScope {
  placement: Placement;
  /** The query parameter naming the scope (`course`), set only for the scoped place. */
  scopeParam?: string | null;
  /** The scope value (a course slug); required for the scoped place, never sent otherwise. */
  scope?: string | null;
}

/** The list params that read one place's ticked items in that place's order. */
export function placeParams(place: PlaceScope): Record<string, string> {
  return place.scopeParam && place.scope
    ? { placement: place.placement, [place.scopeParam]: place.scope }
    : { placement: place.placement };
}

/**
 * One place's hand-made order (`PUT /admin/{collection}/order/{placement}?course=`,
 * round 8, D-151): every live item ticked for the place, first to last. Other places,
 * other courses and the collection's own order are untouched.
 */
export function savePlacementOrder(
  collection: string,
  place: PlaceScope,
  ids: string[],
): Promise<{ placement: Placement; items: OrderEntry[] }> {
  const q =
    place.scopeParam && place.scope
      ? `?${encodeURIComponent(place.scopeParam)}=${encodeURIComponent(place.scope)}`
      : '';
  return api.put(`/admin/${collection}/order/${encodeURIComponent(place.placement)}${q}`, { ids });
}

/**
 * Collection `doc` JSON Schemas (GET /admin/collection-schemas). The backend owns the
 * canonical shape of each collection's rich `doc`; the content editor is generated from
 * the matching entry (industry/project/article/job) so the form, server validation, and
 * the public site never drift. Collections without a backend entry fall back to the static
 * registry in `@/config/collections`.
 */
export function collectionSchemas(): Promise<BlockSchemaRegistry> {
  return api.get<BlockSchemaRegistry>('/admin/collection-schemas');
}
